import { ArrowDownRight, ArrowUpRight, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { cn } from "@/lib/utils";
import { fmtValue, moneyHeader } from "@/lib/analytics";
import { Panel, SectionLabel, StatusBadge } from "@/components/common/ui-kit";

const Delta = ({ k }) => {
  if (k.prev === null || k.prev === undefined || k.value === null) return null;
  const d = k.value - k.prev;
  if (!d) return <span className="text-xs text-slate-400">= periodo prec.</span>;
  const up = d > 0;
  return <span className={cn("inline-flex items-center text-xs font-medium", up ? "text-emerald-600" : "text-rose-600")}>
    {up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}{fmtValue(Math.abs(d), k.unit)} vs prec.</span>;
};

export const KpiStrip = ({ kpis, onDrill }) => (
  <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-line bg-white md:grid-cols-3 xl:grid-cols-5" data-testid="analytics-kpis">
    {kpis.map((k) => (
      <button key={k.key} type="button" disabled={!k.drill} onClick={() => onDrill(k.drill)} data-testid={`kpi-${k.key}`}
        className="group -mb-px -mr-px border-b border-r border-line bg-white p-4 text-left transition-colors duration-150 hover:bg-slate-50 disabled:cursor-default">
        <p className="text-xs text-slate-500">{k.label}</p>
        <p className="mt-1 text-xl font-semibold tracking-tight text-ink" data-testid={`kpi-${k.key}-value`}>{fmtValue(k.value, k.unit)}{k.unit === "money" && <span className="ml-1 text-[11px] font-normal text-slate-400" data-testid={`kpi-${k.key}-net`}>imponibile</span>}</p>
        <div className="mt-1 flex min-h-[18px] flex-wrap items-center gap-x-2"><Delta k={k} />{k.hint && <span className="text-xs text-slate-400">{k.hint}</span>}</div>
      </button>
    ))}
  </div>
);

export const Attention = ({ items, onDrill }) => (
  <Panel className="p-5" data-testid="analytics-attention">
    <SectionLabel>Dove intervenire</SectionLabel>
    {!items.length && <p className="mt-2 text-sm text-slate-500">Nessuna situazione da segnalare nel tuo perimetro.</p>}
    <ul className="mt-2 divide-y divide-line">{items.map((a, i) => (
      <li key={i}><button type="button" onClick={() => onDrill(a.drill)} className="flex w-full items-center gap-3 py-2.5 text-left hover:text-brand-600" data-testid={`attention-${i}`}>
        <StatusBadge tone={a.tone}>{a.count}</StatusBadge>
        <span className="flex-1 text-sm text-ink">{a.label}</span><ChevronRight className="h-4 w-4 text-slate-400" /></button></li>))}</ul>
  </Panel>
);

const Chart = ({ t }) => {
  if (!t.rows.length) return null;
  if (t.chart === "trend") {
    const keys = t.columns.slice(1).filter((c) => c.type === "money");
    const colors = ["#0f172a", "#2563eb", "#10b981"];
    return <div className="h-56" data-testid={`chart-${t.key}`}><ResponsiveContainer><LineChart data={t.rows}><CartesianGrid stroke="#eef2f7" vertical={false} />
      <XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={70} tickFormatter={(v) => fmtValue(v, "int")} />
      <Tooltip formatter={(v, n) => [fmtValue(v, "money"), keys.find((k) => k.key === n)?.label]} />
      {keys.map((k, i) => <Line key={k.key} dataKey={k.key} stroke={colors[i % 3]} strokeWidth={2} dot={false} />)}</LineChart></ResponsiveContainer></div>;
  }
  const col = t.columns.find((c) => c.key === t.chart);
  return <div className="h-48" data-testid={`chart-${t.key}`}><ResponsiveContainer><BarChart data={t.rows}><CartesianGrid stroke="#eef2f7" vertical={false} />
    <XAxis dataKey={t.columns[0].key} tick={{ fontSize: 11 }} interval={0} angle={t.rows.length > 5 ? -20 : 0} height={t.rows.length > 5 ? 50 : 30} />
    <YAxis tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => fmtValue(v, "int")} /><Tooltip formatter={(v) => fmtValue(v, col?.type)} />
    <Bar dataKey={t.chart} fill="#0f172a" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>;
};

export const DataTable = ({ t, onDrill, onCustomer }) => {
  const nav = useNavigate();
  const click = (r) => (r.link ? nav(r.link) : r.drill?.customer ? onCustomer(r.drill.customer) : r.drill?.metric && onDrill(r.drill));
  return (
    <Panel className="p-5" data-testid={`table-${t.key}`}>
      <SectionLabel>{t.title}</SectionLabel>
      {t.note && <p className="mt-1 text-xs text-slate-400">{t.note}</p>}
      {t.chart && <div className="mt-3"><Chart t={t} /></div>}
      <div className="mt-3 overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="border-b border-line text-left text-xs text-slate-500">{t.columns.map((c) => <th key={c.key} className={cn("py-2 pr-3 font-medium", c.type !== "text" && "text-right")}>{moneyHeader(c)}</th>)}</tr></thead>
        <tbody>{t.rows.map((r, i) => (
          <tr key={i} onClick={() => click(r)} data-testid={`table-${t.key}-row-${i}`}
            className={cn("border-b border-line/60 last:border-0", r.warn && "bg-amber-50/70", r.total && "font-semibold", (r.link || r.drill?.metric || r.drill?.customer) && "cursor-pointer hover:bg-slate-50")}>
            {t.columns.map((c, j) => <td key={c.key} className={cn("py-2 pr-3", c.type !== "text" && "text-right tabular-nums", j === 0 && "font-medium text-ink", r.warn && c.key === "notes" && "text-amber-800")}>{fmtValue(r[c.key], c.type)}</td>)}
          </tr>))}
          {!t.rows.length && <tr><td colSpan={t.columns.length} className="py-3 text-slate-500">Nessun dato nel periodo e nei filtri selezionati.</td></tr>}</tbody>
      </table></div>
    </Panel>
  );
};
